import React, { useEffect, useState, useContext, useRef } from "react";
import Page from "components/ui/Page";
import Header from "components/ui/Header";
import { Box, Flex, Grid, Link, Spinner } from "theme-ui";
import { BackArrowIcon } from "components/svg";
import { getQuote } from "api/Quotes";
import { trackActivity } from "api/ActivityTracker";
import { useNavigate, useParams } from "react-router-dom";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { themevals } from "theme/themevals";
import BasicButton from "components/forms/BasicButton";
import { Tab, Tabs } from "components/ui/Tabs";
import { CheckMarkIcon, TrashIcon, QuestionMarkIcon, EllipsisIcon, PlusIcon, ProductsIcon } from "components/svg";
import H5 from "components/typography/H5";
import Subtext from "components/typography/Subtext";
import TextInput from "components/forms/TextInput";
import SelectDate from "components/forms/SelectDate";
import moment from "moment";
import Separator from "components/ui/Separator";
import SelectInput from "components/forms/SelectInput";
import { useToast } from "components/toast";
import UserContext from "context/User";
import { getClients } from "api/Clients";
import MultiText from "components/forms/MultiText";
import { createColumnHelper } from "@tanstack/react-table";
import TableBasic from "components/ui/Table";
import DropDownMenu from "components/forms/DropDown/DropDownMenu";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "components/ui/Modal";
import { ModalIcon, ModalSubtitle, ModalTitle, ModalTitleWrapper } from "components/ui/Header/styled";
import P from "components/typography/P";
import { Button } from "theme-ui";
import { getAllDepots, getDepot, getDepots } from "api/Providers/Depots";
import SearchField from "components/forms/SearchField";
import Checkbox from "components/forms/Checkbox";
import { updateQuote } from "api/Quotes";

interface Product {
  id: string;
  name: string;
  addon: boolean;
  unit?: string;
  baseCost?: number;
  quantity?: number;
}

interface Depot {
  id: string;
  name: string;
  provider: { name: string };
  products: Product[];
}

interface QuoteItem {
  id?: string;
  quantity: number;
  type?: string;
  entityId?: string;
  depotId: string;
  markup: number;
  cost: number;
}

export interface UpdateQuoteData {
  projectName?: string;
  serviceDate?: string;
  primaryClient?: { id: string };
  siteContactName?: string;
  siteContactEmail?: string;
  contactsCC?: string[];
  contactsBCC?: string[];
  items?: QuoteItem[][]; // Array of arrays
}

const QuoteEdit = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { id } = params;
  const [quote, setQuote] = useState<any>(null);
  const [companyClientOptions, setCompanyClientOptions] = useState<{ value: string; label: string }[]>([]);
  const [tableItems, setTableItems] = useState<any[]>([]);
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [selectedDepot, setSelectedDepot] = useState<Depot | null>(null);
  const [depotServiceOptions, setDepotServiceOptions] = useState<any[]>([]);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [selectedService, setSelectedService] = useState<any>(null);
  const [selectedAddons, setSelectedAddons] = useState<any[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<any>(null);
  const [canOpenEditModal, setCanOpenEditModal] = useState<boolean>(false);
  const [providerOptions, setProviderOptions] = useState<any[]>([]);
  const [depotOptions, setDepotOptions] = useState<any[]>([]);
  const [addOnOpen, setAddOnOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [newAddons, setNewAddons] = useState<any[]>([]);
  const [originalTableItems, setOriginalTableItems] = useState<any[]>([]);

  const { user } = useContext<any>(UserContext);
  const columnHelper = createColumnHelper();
  const toast = useToast();
  const hasTrackedView = useRef(false);

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | Quote";
    _init();
  }, []);

  const _init = async () => {
    const quoteRes = await getQuote({ data: { id } });
    if (quoteRes.id) {
      setQuote(quoteRes);
      
      // Track view after successful load
      if (!hasTrackedView.current) {
        hasTrackedView.current = true;
        void trackActivity({
          module: "quotes",
          action: "view_quote_edit",
          actionType: "view",
          userId: user?.id || null,
          companyId: user?.company?.id || null,
          entityType: "quote",
          entityId: quoteRes.id,
          metadata: {},
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        });
      }
      
      const allItems = quoteRes.items?.flat() || [];

      const groupedByItemGroup: Record<number, any[]> = {};
      allItems.forEach((item: any) => {
        if (!groupedByItemGroup[item.itemGroup]) {
          groupedByItemGroup[item.itemGroup] = [];
        }
        groupedByItemGroup[item.itemGroup].push(item);
      });

      const structuredItems = Object.values(groupedByItemGroup).map((group: any[]) => {
        const service = group.find((item) => item.type === "service");
        const product = group.find((item) => item.type === "product");
        const addons = group.filter((item) => item.type === "addon-product");

        console.log(addons);

        return {
          ...service,
          product: product
            ? {
                id: product.id,
                name: product.name,
                description: product.description,
                unit: product.unit,
                entityId: product.entityId,
              }
            : null,
          cost: product?.cost ?? 0,
          markup: product?.markup ?? 0,
          price: product?.price ?? 0,
          quantity: product?.quantity ?? 0,
          serviceId: service?.entityId,
          addons: addons.map((addon) => ({
            id: addon.id,
            name: addon.name,
            description: addon.description,
            unit: addon.unit,
            baseCost: addon.cost,
            markup: addon.markup,
            price: addon.price,
            quantity: addon.quantity,
            position: addon.position,
            addonId: addon.entityId,
          })),
        };
      });

      setTableItems(structuredItems);
      setOriginalTableItems(structuredItems);
    }
    const clientRes = await getClients({});
    setCompanyClientOptions(clientRes.items.map((p: any) => ({ value: p.id, label: p.name })));
  };

  const submitUpdate = async (values: any) => {
    const mappedItems = tableItems.map((service) => {
      const addonItems = (service.addons || [])
        .filter((addon: any) => addon.quantity > 0)
        .map((addon: any) => {
          const addonItem: any = {
            quantity: addon.quantity,
            price: Number(addon.price),
            cost: Number(addon.baseCost),
            depotId: service.depot?.id,
            entityId: addon.addonId,
            type: "addon-product",
          };
          // Only include id if it is NOT equal to addonId (i.e., it's a real quote item ID)
          if (addon.id && addon.id !== addon.addonId) addonItem.id = addon.id;
          return addonItem;
        });

        console.log("addonItems", addonItems);

      const serviceItem: any = {
        id: service.id,
        quantity: 1,
        cost: 0,
        markup: 0,
        depotId: service.depot?.id,
        entityId: service.serviceId,
        type: "service",
      };

      const productItem: any = {
        id: service.product.id,
        quantity: service.quantity,
        cost: Number(service.cost),
        markup: Number(service.markup),
        depotId: service.depot?.id,
        entityId: service.product.entityId,
        price: service.price || 0,
        type: "product",
      };

      return [serviceItem, productItem, ...addonItems];
    });

    const servicesChanged =
      tableItems.length !== originalTableItems.length ||
      tableItems.some((current) => {
        const original = originalTableItems.find((s) => s.id === current.id);
        if (!original) return true;

        const fieldsMatch =
          original.quantity === current.quantity &&
          Number(original.price) === Number(current.price) && 
          original.depot?.id === current.depot?.id &&
          original.serviceId === current.serviceId;

        const currentAddons = (current.addons || []).filter((a: any) => a.quantity > 0);
        const originalAddons = (original.addons || []).filter((a: any) => a.quantity > 0);

        const addonsMatch =
          currentAddons.length === originalAddons.length &&
          currentAddons.every((a: any) => {
            const match = originalAddons.find((o: any) => o.addonId === a.addonId);
            return (
              match &&
              a.quantity === match.quantity &&
              Number(a.baseCost) === Number(match.baseCost) &&
              Number(a.markup) === Number(match.markup) &&
              Number(a.price) === Number(match.price) // <-- compare price too
            );
          });

        return !(fieldsMatch && addonsMatch);
      });

    const updateData: UpdateQuoteData = {
      projectName: values.projectName,
      primaryClient: { id: values.primaryClient },
      siteContactName: values.siteContactName,
      siteContactEmail: values.siteContactEmail,
      contactsCC: values.cc,
      contactsBCC: values.bcc,
      ...(servicesChanged ? { items: mappedItems } : {}),
    };

    if (values.startDate) {
      updateData.serviceDate = moment.unix(values.startDate).utc().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
    }
    console.log('mappedItems', mappedItems);

    const res = await updateQuote({ data: { id }, update: updateData });

    if (res.id) {
      // Track what changed
      const changedFields: string[] = [];
      
      if (values.projectName !== quote.projectName) changedFields.push("projectName");
      if (values.primaryClient !== quote.primaryClient?.id) changedFields.push("primaryClient");
      
      // Normalize empty strings and null for comparison
      const normalizedSiteContactName = values.siteContactName || null;
      const originalSiteContactName = quote.siteContactName || null;
      if (normalizedSiteContactName !== originalSiteContactName) changedFields.push("siteContactName");
      
      const normalizedSiteContactEmail = values.siteContactEmail || null;
      const originalSiteContactEmail = quote.siteContactEmail || null;
      if (normalizedSiteContactEmail !== originalSiteContactEmail) changedFields.push("siteContactEmail");
      
      const ccChanged = JSON.stringify(values.cc || []) !== JSON.stringify(quote.contactsCC || []);
      if (ccChanged) changedFields.push("contactsCC");
      
      const bccChanged = JSON.stringify(values.bcc || []) !== JSON.stringify(quote.contactsBCC || []);
      if (bccChanged) changedFields.push("contactsBCC");
      
      if (values.startDate && quote.serviceDate) {
        const newDate = moment.unix(values.startDate).format("YYYY-MM-DD");
        const oldDate = moment(quote.serviceDate).format("YYYY-MM-DD");
        if (newDate !== oldDate) changedFields.push("serviceDate");
      } else if (values.startDate && !quote.serviceDate) {
        changedFields.push("serviceDate");
      }
      
      if (servicesChanged) changedFields.push("items");
      
      void trackActivity({
        module: "quotes",
        action: "update_quote",
        actionType: "update",
        userId: user?.id || null,
        companyId: user?.company?.id || null,
        entityType: "quote",
        entityId: res.id,
        metadata: {
          changedFields,
        },
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
      
      setQuote(res);
      toast.add("Quote updated.", "var(--theme-ui-colors-success)");
      navigate(`/quotes/${res.id}`);
    }
  };

  const _validate = Yup.object().shape({
    projectName: Yup.string().required("Project name is required."),
  });

  const toggleEditModal = (item?: any) => {
    console.log(item);
    if (item) {
      const bulkProductId = item.product.id;
      const addOnIds = item.addons.map((addon: any) => addon.addonId);
      const combinedProductIds = [bulkProductId, ...addOnIds].join(",");

      if (user.role !== null) {
        getDepotDetails(item.depot.id, item.depot.provider.id, combinedProductIds);
      } else {
        setSelectedDepot(item.depot.id);
        setDepotServiceOptions([{ value: item.serviceId, label: item.name }]);
      }

      setSelectedRow(item);
      setSelectedAddons(item.addons);
      setSelectedService(item.serviceId);
      setSelectedProvider(item.depot.provider.id);
      setCanOpenEditModal(true);
    } else {
      setSelectedRow(null);
      setSelectedDepot(null);
      setEditModalOpen(false);
      setCanOpenEditModal(false);
    }
  };

  const submitRemoveItem = async (item: any) => {
    console.log("Remove item", item);
  };

  const getDepotDetails = async (depotId: string, providerId: string, combinedProductIds?: any) => {
    const depotRes = await getDepot({ data: { id: depotId }, provider: { id: providerId } });

    if (depotRes.id) {
      setSelectedDepot(depotRes);

      const serviceOptions = depotRes.services.map((service: any) => ({
        value: service.id,
        label: service.name,
      }));

      setDepotServiceOptions(serviceOptions);
      const combinedServiceIds = (depotRes.services || []).map((service: any) => service.id);
      if (combinedProductIds) {
        getMatchingDepots(combinedProductIds, combinedServiceIds.join(","), providerId);
      }
    }
  };

  const getMatchingDepots = async (combinedIds: string, serviceIds: string, providerId: string) => {
    const depotRes = await getAllDepots({ query: { joins: ["products", "services"], filters: { $or: { "products.id": `${combinedIds}:in`, "services.id": `${serviceIds}:in` } } } });
    if (depotRes.items) {
      const uniqueProviders = Array.from(new Map(depotRes.items.map((item: any) => [item.provider.id, { label: item.provider.name, value: item.provider.id }])).values());

      setProviderOptions(uniqueProviders);

      const uniqueDepots = depotRes.items
        .filter((depot: any) => depot.provider.id === providerId)
        .map((depot: any) => ({
          value: depot.id,
          label: depot.name,
        }));
      setDepotOptions(uniqueDepots);
    }
  };

  const submitItemEdit = async (values: any) => {
    const relatedServiceOption = depotServiceOptions.find((option) => option.value === values.relatedService);

    const relatedServiceLabel = relatedServiceOption?.label || "";

    let depotRes = null;

    if (user.role !== null) {
      depotRes = await getDepot({ data: { id: values.depot }, provider: { id: values.provider } });
    } else {
      depotRes = selectedRow.depot;
    }

    const services = [
      {
        id: selectedRow.id,
        serviceId: values.relatedService,
        quantity: values.quantity,
        markup: values.markup,
        price: values.price,
        cost: values.unitCost,
        depot: depotRes,
        name: relatedServiceLabel,
        product: selectedRow.product,
        relatedService: values.relatedService,
        addons: values.addons,
        provider: values.provider,
      },
    ];

    setTableItems((prev) => prev.map((row) => (row.id === selectedRow.id ? services[0] : row)));
    toggleEditModal();
  };

  const toggleAddOnModal = (item?: any) => {
    setSearchTerm("");
    setAddOnOpen(!addOnOpen);
  };

  useEffect(() => {
    if (canOpenEditModal && selectedRow && selectedDepot) {
      setEditModalOpen(true);
      setCanOpenEditModal(false); // Reset flag after opening
    }
  }, [canOpenEditModal, selectedRow, selectedDepot]);

  const handleProviderChange = async (providerId: string, setFieldValue: any, fieldName: string) => {
    setFieldValue(fieldName, providerId); // Set provider FIRST

    const res = await getDepots({ provider: { id: providerId } });

    const depotOptions = (Array.isArray(res.items) ? res.items : [])
      .filter((depot: any) => !depot?.deletedAt)
      .map((depot: any) => ({
      value: depot.id,
      label: depot.name,
    }));

    setDepotOptions(depotOptions);

    if (depotOptions.length > 0) {
      const firstDepotId = depotOptions[0].value;
      setFieldValue("depot", firstDepotId);
      await getDepotDetails(firstDepotId, providerId);
    }
  };

  const columns = [
    columnHelper.accessor((row: any) => row?.name, {
      id: "relatedService",
      cell: (info: any) => <Box sx={{ color: "#000", fontWeight: "500" }}>{info.getValue()}</Box>,
      header: () => <span>Related Service</span>,
    }),
    columnHelper.accessor((row: any) => row?.product.name, {
      id: "productName",
      cell: (info: any) => info.getValue(),
      header: () => <span>Bulk Product</span>,
    }),
    columnHelper.accessor((row: any) => row?.addons, {
      id: "addons",
      cell: (info: any) => {
        const addons = info.getValue();
        return Array.isArray(addons) && addons.filter((addon: any) => addon.quantity > 0).length > 0
          ? addons
              .filter((addon: any) => addon.quantity > 0)
              .map((addon: any) => addon.name)
              .join(", ")
          : "None";
      },
      header: () => <span>Add-on Products</span>,
    }),
    ...(user.role !== null
      ? [
          columnHelper.accessor((row: any) => row?.depot.provider.name, {
            id: "providerName",
            cell: (info: any) => info.getValue(),
            header: () => <span>Provider</span>,
          }),
        ]
      : []),
    columnHelper.accessor((row: any) => "actions", {
      id: "actions",
      cell: (info: any) => {
        const items = [
          { label: "Edit", onClick: () => toggleEditModal(info.row.original) },
          ...(tableItems.length > 1 ? [{ label: "Remove", onClick: () => submitRemoveItem(info.row.original), style: { color: "var(--theme-ui-colors-danger)" } }] : []),
        ];

        return (
          <Box>
            <DropDownMenu toggleComponent={<EllipsisIcon fill={"#818181"} width="20px" height="20px" />} items={items} />
          </Box>
        );
      },
      header: () => null,
      enableSorting: false,
      meta: {
        style: { width: "10px" },
      },
    }),
  ];

  return (
    <>
      <Header></Header>
      {!quote && (
        <Box sx={{ textAlign: "center", paddingTop: "30px" }}>
          <Spinner size={25} strokeWidth={3} title="loading" />
        </Box>
      )}
      {quote && (
        <Page
          title="Edit Quote"
          subtitle="Edit the Quote to update it's details."
          backAction={
            <Link variant="back" href={`/quotes/${quote.id}`}>
              <BackArrowIcon />
              {`Back to Quote`}
            </Link>
          }
        >
          <Formik
            initialValues={{
              projectName: quote.projectName,
              startDate: quote.serviceDate ? moment.utc(quote.serviceDate).unix() : null,
              siteContactName: quote.siteContactName ? quote.siteContactName : "",
              siteContactEmail: quote.siteContactEmail ? quote.siteContactEmail : "",
              primaryClient: quote.primaryClient.id,
              cc: quote.contactsCC || [],
              bcc: quote.contactsBCC || [],
            }}
            onSubmit={submitUpdate}
            validationSchema={_validate}
          >
            {({ isSubmitting, errors, values, submitCount, setFieldValue, handleSubmit }) => (
              <Form noValidate autoComplete="off">
                <Tabs
                  vertical
                  stickyFooter
                  footer={
                    <Flex sx={{ gap: "10px", flexGrow: 1, justifyContent: "flex-end" }}>
                      <Link href={`/quotes/${quote.id}`} sx={{ ...themevals.buttons.secondaryOutline, textDecoration: "none", margin: "0 10px 0 0" }}>
                        Cancel
                      </Link>
                      <BasicButton type="submit" $submitting={isSubmitting} styles={{ margin: "0 10px 0 0" }} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
                        {!isSubmitting && <CheckMarkIcon fill={"#fff"} />}
                        Save Quote
                      </BasicButton>
                    </Flex>
                  }
                >
                  <Tab itemKey="general">
                    <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
                      <Box>
                        <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
                          Basic Info
                        </H5>
                        <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                          Edit basic information about the Quote.
                        </Subtext>
                      </Box>
                      <Box>
                        <Flex sx={{ gap: "20px" }}>
                          <TextInput
                            name="projectName"
                            label="Project Name"
                            value={values.projectName}
                            onChange={(val: any) => setFieldValue("projectName", val)}
                            $errors={errors.projectName && submitCount > 0 ? errors.projectName : null}
                            $customStyles={{ flex: 1, marginBottom: "0px" }}
                          />
                          <SelectDate
                            name="startDate"
                            label="Estimated Service Date"
                            value={values.startDate}
                            onChange={(val: any) => setFieldValue("startDate", val)}
                            $errors={errors.startDate && submitCount > 0 ? errors.startDate : null}
                            $customStyles={{ width: "50%", flexGrow: 1, marginBottom: "0px" }}
                            $removeMargin={true}
                          />
                        </Flex>
                      </Box>
                    </Grid>
                    <Separator />
                    <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
                      <Box>
                        <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
                          Contacts
                        </H5>
                        <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                          Edit contacts associated with the Quote.
                        </Subtext>
                      </Box>
                      <Box>
                        <Flex sx={{ gap: "20px" }}>
                          <TextInput
                            name="siteContactName"
                            label="Site Contact Name"
                            value={values.siteContactName}
                            onChange={(val: any) => setFieldValue("siteContactName", val)}
                            $errors={errors.siteContactName && submitCount > 0 ? errors.siteContactName : null}
                            $customStyles={{ flex: 1 }}
                          />
                          <TextInput
                            name="siteContactEmail"
                            label="Site Contact Email"
                            value={values.siteContactEmail}
                            onChange={(val: any) => setFieldValue("siteContactEmail", val)}
                            $errors={errors.siteContactEmail && submitCount > 0 ? errors.siteContactEmail : null}
                            $customStyles={{ flex: 1 }}
                          />
                        </Flex>
                        <Flex sx={{ gap: "20px" }}>
                          <Flex sx={{ flex: 1 }}>
                            <SelectInput
                              name="primaryClient"
                              label={user.role === null ? "Primary Contact" : " Primary Client"}
                              value={values.primaryClient}
                              options={companyClientOptions}
                              onChange={(val: any) => setFieldValue("primaryClient", val)}
                              $errors={errors.primaryClient && submitCount > 0 ? errors.primaryClient : null}
                              $customStyles={{ width: "100%" }}
                            />
                          </Flex>
                          <Flex sx={{ flex: 1 }}></Flex>
                        </Flex>
                        {user.role && (
                          <Flex sx={{ gap: "20px", marginTop: "30px" }}>
                            <Flex sx={{ flex: 1 }}>
                              <MultiText
                                name="cc"
                                label="CC"
                                value={values.cc}
                                onChange={(val: string[]) => setFieldValue("cc", val)}
                                $errors={errors.cc && submitCount > 0 ? errors.cc : null}
                                $customStyles={{ width: "100%" }}
                              />
                            </Flex>
                            <Flex sx={{ flex: 1 }}>
                              <MultiText
                                name="bcc"
                                label="BCC"
                                value={values.bcc}
                                onChange={(val: string[]) => setFieldValue("bcc", val)}
                                $errors={errors.bcc && submitCount > 0 ? errors.bcc : null}
                                $customStyles={{ width: "100%" }}
                              />
                            </Flex>
                          </Flex>
                        )}
                      </Box>
                    </Grid>
                    <Separator />
                    <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
                      <Box>
                        <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
                          Items
                        </H5>
                        <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                          Edit the products and services associated with the Quote.
                        </Subtext>
                      </Box>
                      <Box>
                        {tableItems && (
                          <TableBasic
                            data={tableItems}
                            columns={columns}
                            enablePaging={false}
                            enableSearch={false}
                            customStyles={{ border: `1px solid ${themevals.colors.inputOutline}` }}
                            disableSorting
                          />
                        )}
                      </Box>
                    </Grid>
                  </Tab>
                </Tabs>
              </Form>
            )}
          </Formik>
          <Modal isOpen={editModalOpen} toggle={() => toggleEditModal()} centered size={user.role !== null ? "md" : "sm"}>
            <Formik
              initialValues={{
                relatedService: selectedService,
                addons: selectedAddons,
                provider: selectedProvider,
                depot: selectedDepot?.id,
                quantity: selectedRow?.quantity,
                price: selectedRow?.price,
                cost: selectedRow?.cost,
                markup: selectedRow?.markup,
                unitCost:
                  Number(selectedRow?.cost) > 0
                    ? Number(selectedRow.cost)
                    : selectedRow?.price && selectedRow?.markup
                    ? Number((Number(selectedRow.price) / (1 + Number(selectedRow.markup) / 100)).toFixed(2))
                    : 0,
                product: selectedRow?.product,
              }}
              onSubmit={(values) => submitItemEdit(values)}
            >
              {({
                isSubmitting,
                values,
                setFieldValue,
                errors,
                submitCount,
              }: {
                isSubmitting: boolean;
                submitCount: number;
                values: any;
                setFieldValue: (name: string, val?: any) => void;
                errors: any;
              }) => {
                const availableAddons =
                  selectedDepot?.products?.filter((product) => {
                    if (!product.addon) return false;
                    const existing = values.addons.find((addon: any) => addon.addonId === product.id);
                    return !existing || existing.quantity === 0;
                  }) ?? [];

                const submitRemoveAddonItem = (addonToRemove: any) => {
                  const updated = values.addons.map((addon: any) => (addon.addonId === addonToRemove.addonId ? { ...addon, quantity: 0 } : addon));
                  setFieldValue("addons", updated);
                  setSelectedAddons(updated);
                };

                useEffect(() => {
                  const mergedAddons = [...values.addons];

                  selectedAddons.forEach((newAddon) => {
                    const existingIndex = mergedAddons.findIndex((a) => a.addonId === newAddon.addonId);

                    if (existingIndex > -1) {
                      mergedAddons[existingIndex] = {
                        ...mergedAddons[existingIndex],
                        quantity: newAddon.quantity ?? mergedAddons[existingIndex].quantity,
                        markup: newAddon.markup ?? mergedAddons[existingIndex].markup,
                        baseCost: newAddon.baseCost ?? mergedAddons[existingIndex].baseCost,
                        price: newAddon.price ?? mergedAddons[existingIndex].price,
                      };
                    } else {
                      mergedAddons.push({ ...newAddon, quantity: newAddon.quantity || 1 });
                    }
                  });

                  setFieldValue("addons", mergedAddons);
                }, [selectedAddons]);

                const relatedServiceIdsInUse = tableItems.filter((row) => row.id !== selectedRow?.id).map((row) => row.serviceId);

                const filteredServiceOptions = depotServiceOptions.filter((option) => !relatedServiceIdsInUse.includes(option.value));

                return (
                  <Form noValidate autoComplete="off">
                    <ModalHeader toggle={() => toggleEditModal()}>
                      <ModalIcon>
                        <ProductsIcon fill={"000"} />
                      </ModalIcon>
                      <ModalTitleWrapper>
                        <ModalTitle>Edit Quote item</ModalTitle>
                        <ModalSubtitle>{"Edit an item related to a Quote."}</ModalSubtitle>
                      </ModalTitleWrapper>
                    </ModalHeader>
                    <ModalBody>
                      <Flex sx={{ gap: "20px", flexDirection: user.role !== null ? "row" : "column" }}>
                        <Box sx={{ flex: 1 }}>
                          <SelectInput
                            name="bulkProduct"
                            label="Bulk Product"
                            value={values.product.id}
                            options={[{ value: values.product.id, label: values.product.name }]}
                            $errors={errors.bulkProduct && submitCount > 0 ? errors.bulkProduct : null}
                            $customStyles={{ flexGrow: 1, width: "100%" }}
                            disabled
                          />
                        </Box>
                        {user.role === null && (
                          <Box sx={{ flex: 1 }}>
                            <SelectInput
                              name="relatedService"
                              label="Related Service"
                              value={values.relatedService}
                              options={filteredServiceOptions}
                              $errors={errors.relatedService && submitCount > 0 ? errors.relatedService : null}
                              $customStyles={{ flexGrow: 1, width: "100%" }}
                              disabled={user.role === null}
                              onChange={(val: any) => setFieldValue("relatedService", val)}
                            />
                          </Box>
                        )}
                        {user.role === null && (
                          <Box sx={{ flex: 1 }}>
                            <TextInput
                              name="quantity"
                              label="Quantity"
                              type="number"
                              value={values.quantity}
                              onChange={(val: any) => setFieldValue("quantity", val)}
                              $errors={errors?.quantity && submitCount > 0 ? errors.quantity : null}
                              suffix={values.product.unit}
                            />
                          </Box>
                        )}
                        {user.role !== null && (
                          <Box sx={{ flex: 1 }}>
                            <SelectInput
                              name="relatedService"
                              label="Related Service"
                              value={values.relatedService}
                              options={filteredServiceOptions}
                              $errors={errors.relatedService && submitCount > 0 ? errors.relatedService : null}
                              $customStyles={{ flexGrow: 1, width: "100%" }}
                              disabled={user.role === null}
                              onChange={(val: any) => setFieldValue("relatedService", val)}
                            />
                          </Box>
                        )}
                      </Flex>
                      {user.role !== null && (
                        <Flex sx={{ gap: "20px", marginTop: "30px" }}>
                          <Box sx={{ flex: 1 }}>
                            <SelectInput
                              name="provider"
                              label="Provider"
                              value={values.provider}
                              options={providerOptions}
                              $errors={errors.provider && submitCount > 0 ? errors.provider : null}
                              $customStyles={{ flexGrow: 1, width: "100%" }}
                              onChange={(val: any) => handleProviderChange(val, setFieldValue, "provider")}
                            />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <SelectInput
                              name="depot"
                              label="Depot"
                              value={values.depot}
                              options={depotOptions}
                              $errors={errors.relatedService && submitCount > 0 ? errors.relatedService : null}
                              $customStyles={{ flexGrow: 1, width: "100%" }}
                            />
                          </Box>
                        </Flex>
                      )}
                      {user.role !== null && (
                        <Flex sx={{ gap: "20px", marginTop: "30px" }}>
                          <Box sx={{ flex: 1 }}>
                            <TextInput
                              name="cost"
                              label="Cost"
                              type="number"
                              value={values.cost}
                              onChange={(val: any) => setFieldValue("cost", val)}
                              prefix={"$"}
                              $errors={errors?.cost && submitCount > 0 ? errors.cost : null}
                            />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <TextInput
                              name="price"
                              label="Price"
                              type="number"
                              value={values.price}
                              onChange={(val: any) => setFieldValue("price", val)}
                              prefix={"$"}
                              $errors={errors?.price && submitCount > 0 ? errors.price : null}
                            />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <TextInput
                              name="quantity"
                              label="Quantity"
                              type="number"
                              value={values.quantity}
                              onChange={(val: any) => setFieldValue("quantity", val)}
                              $errors={errors?.quantity && submitCount > 0 ? errors.quantity : null}
                            />
                          </Box>
                        </Flex>
                      )}
                      <Flex>
                        <Subtext fontFamily="Poppins" marginTop="0px" marginBottom="8px">
                          Add-on Products
                        </Subtext>
                      </Flex>
                      <Flex sx={{ border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: "6px", flexDirection: "column", marginBottom: "30px" }}>
                        <Flex sx={{ padding: "20px", gap: "20px", flexDirection: "column" }}>
                          {values.addons.filter((addon: any) => addon.quantity > 0).length > 0 &&
                            values.addons.map((addon: any, index: number) =>
                              addon.quantity > 0 ? (
                                <Flex
                                  key={index}
                                  sx={{
                                    gap: "10px",
                                    justifyContent: "flex-start",
                                    borderRadius: "5px",
                                    border: `1px solid ${themevals.colors.mdgrey}`,
                                    padding: "10px 15px",
                                    width: "100%",
                                  }}
                                >
                                  <Flex sx={{ flex: 1, alignItems: "center", justifyContent: "flex-start" }}>
                                    <P fontFamily="Poppins" marginTop="0px" marginBottom="0px" fontSize="14px" fontWeight="500">
                                      {addon.name}
                                    </P>
                                  </Flex>
                                  <Flex sx={{ alignItems: "center", justifyContent: "flex-end", flex: user.role !== null ? 2 : 1, gap: "20px" }}>
                                     {user.role !== null && (
                                      <Flex sx={{ alignItems: "center", justifyContent: "flex-end" }}>
                                        <TextInput
                                          name={`addons[${index}].baseCost`}
                                          value={addon.baseCost}
                                          type="number"
                                          label="Cost"
                                          prefix={"$"}
                                          $customStyles={{ marginBottom: "0px" }}
                                          onChange={(val: any) => setFieldValue(`addons[${index}].baseCost`, val)}
                                          $errors={errors?.addons?.[index].baseCost && submitCount > 0 ? errors?.addons?.[index].baseCost : null}
                                        />
                                      </Flex>
                                    )}
                                    {user.role !== null && (
                                      <Flex sx={{ alignItems: "center", justifyContent: "flex-end" }}>
                                        <TextInput
                                          name={`addons[${index}].price`}
                                          value={addon.price}
                                          type="number"
                                          label="Price"
                                          prefix={"$"}
                                          $customStyles={{ marginBottom: "0px" }}
                                          onChange={(val: any) => setFieldValue(`addons[${index}].price`, val)}
                                          $errors={errors?.addons?.[index].price && submitCount > 0 ? errors?.addons?.[index].price : null}
                                        />
                                      </Flex>
                                    )}
                                    <Flex sx={{ alignItems: "center", justifyContent: "flex-end" }}>
                                      <TextInput
                                        name={`addons[${index}].quantity`}
                                        value={addon.quantity}
                                        type="number"
                                        label={user.role !== null ? "Quantity" : ""}
                                        min={1}
                                        suffix={addon.unit}
                                        $customStyles={{ marginBottom: "0px" }}
                                        onChange={(val: any) => setFieldValue(`addons[${index}].quantity`, val)}
                                        $errors={errors?.addons?.[index].quantity && submitCount > 0 ? errors?.addons?.[index].quantity : null}
                                        onKeyDown={(e: any) => {
                                          if (e.key === "0" && (e.currentTarget.value === "" || e.currentTarget.value === "0")) {
                                            e.preventDefault();
                                          }
                                        }}
                                        onBlur={(e: any) => {
                                          const val = parseFloat(e.target.value);
                                          if (isNaN(val) || val < 1) {
                                            setFieldValue(`addons[${index}].quantity`, 1);
                                          }
                                        }}
                                      />
                                    </Flex>
                                  </Flex>
                                  {user.role !== null && (
                                    <Flex sx={{ alignItems: "flex-end", justifyContent: "flex-end", paddingLeft: "10px", alignSelf: "stretch", width: "30px" }}>
                                      <Flex sx={{ alignSelf: "stretch", alignItems: "flex-end", paddingBottom: "10px" }}>
                                        <Box sx={{ cursor: "pointer" }} onClick={() => submitRemoveAddonItem(addon)}>
                                          <TrashIcon fill={themevals.colors.dkgrey} width="14px" />
                                        </Box>
                                      </Flex>
                                    </Flex>
                                  )}
                                </Flex>
                              ) : null
                            )}
                          {values.addons.filter((addon: any) => addon.quantity > 0).length === 0 && (
                            <P fontFamily="Poppins" fontSize="14px" color={`${themevals.colors.dkgrey}`}>
                              There are no add-on products associated with this item.
                            </P>
                          )}
                        </Flex>
                        {availableAddons.length > 0 && (
                          <Flex sx={{ borderTop: `1px solid ${themevals.colors.inputOutline}`, padding: "20px", justifyContent: "flex-end" }}>
                            <BasicButton
                              type="button"
                              scheme="secondaryOutline"
                              $submitting={isSubmitting}
                              $iconHeight="14px"
                              $iconWidth="14px"
                              $loadingColor="#000"
                              keepText={true}
                              onClick={() => toggleAddOnModal()}
                            >
                              {!isSubmitting && <PlusIcon fill={"#000"} />}
                              Add product
                            </BasicButton>
                          </Flex>
                        )}
                      </Flex>
                    </ModalBody>
                    <ModalFooter>
                      <Button variant="secondaryOutline" type="button" onClick={() => toggleEditModal()} style={{ marginRight: "10px" }}>
                        Cancel
                      </Button>
                      <BasicButton type="submit" $submitting={isSubmitting} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
                        {!isSubmitting && <CheckMarkIcon fill={"#fff"} />}
                        Save item
                      </BasicButton>
                    </ModalFooter>
                  </Form>
                );
              }}
            </Formik>
          </Modal>
          <Modal isOpen={addOnOpen} toggle={() => toggleAddOnModal()} centered size={"sm"}>
            <Formik
              initialValues={{ relatedService: selectedRow?.name, addons: selectedRow?.addons }}
              onSubmit={() => {
                setAddOnOpen(false);
              }}
              enableReinitialize
            >
              {({
                isSubmitting,
                values,
                setFieldValue,
                submitCount,
                errors,
              }: {
                isSubmitting: boolean;
                values: any;
                setFieldValue: (name: string, val?: any) => void;
                submitCount: number;
                errors: any;
              }) => {
                const availableAddons =
                  selectedDepot?.products?.filter((product) => {
                    if (!product.addon) return false;
                    const existing = selectedAddons.find((addon: any) => addon.addonId === product.id);
                    return !existing || existing.quantity === 0;
                  }) ?? [];

                const toggleSelectAll = () => {
                  if (newAddons.length === availableAddons.length) {
                    // If all are selected, deselect all
                    setNewAddons([]);
                  } else {
                    // Select all available add-ons
                    setNewAddons(
                      availableAddons.map((product) => ({
                        ...product,
                        addonId: product.id,
                        quantity: 1,
                        baseCost: product.baseCost,
                      }))
                    );
                  }
                };

                const handleAddSelectedAddons = () => {
                  setSelectedAddons((prevAddons) => [...prevAddons, ...newAddons]); // Merge selections into first modal
                  setNewAddons([]); // Clear temporary selections
                  setAddOnOpen(false); // Close the modal
                };

                const handleAddonSelect = (product: any) => {
                  const existing = selectedAddons.find((addon: any) => addon.addonId === product.id);
                  setNewAddons((prev) =>
                    prev.some((addon) => addon.id === product.id)
                      ? prev.filter((addon) => addon.id !== product.id) // Remove if already selected
                      : [
                          ...prev,
                          {
                            ...product,
                            addonId: product.id,
                            quantity: 1,
                            baseCost: existing?.baseCost,
                            markup: existing?.markup,
                          },
                        ]
                  );
                };

                return (
                  <Form noValidate autoComplete="off">
                    <ModalHeader toggle={() => toggleAddOnModal()}>
                      <ModalIcon>
                        <ProductsIcon fill={"000"} />
                      </ModalIcon>
                      <ModalTitleWrapper>
                        <ModalTitle>Add products</ModalTitle>
                        <ModalSubtitle>{"Add available products at this depot."}</ModalSubtitle>
                      </ModalTitleWrapper>
                    </ModalHeader>
                    <ModalBody>
                      <SearchField value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} width="100%" />
                      <Flex sx={{ marginTop: "20px", marginBottom: "10px", alignItems: "center", paddingLeft: "11px" }}>
                        <Checkbox label="Select All" onChange={toggleSelectAll} checked={newAddons.length === availableAddons.length && availableAddons.length > 0} />
                      </Flex>
                      {availableAddons.map((product, idx) => (
                        <Flex
                          key={product.id}
                          sx={{
                            gap: "1rem",
                            alignItems: "center",
                            marginBottom: "10px",
                            border: `1px solid ${values.addons.includes(product.id) ? "black" : themevals.colors.mdgrey}`,
                            borderRadius: "6px",
                            height: "38px",
                            padding: "0px 10px",
                          }}
                        >
                          <Checkbox label={product.name} checked={newAddons.some((addon) => addon.id === product.id)} onChange={() => handleAddonSelect(product)} labelSize="14px" />
                        </Flex>
                      ))}
                    </ModalBody>
                    <ModalFooter>
                      <Button variant="secondaryOutline" type="button" onClick={() => setAddOnOpen(false)} style={{ marginRight: "10px" }}>
                        Cancel
                      </Button>
                      <BasicButton type="button" onClick={() => handleAddSelectedAddons()} $submitting={isSubmitting} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
                        {!isSubmitting && <PlusIcon fill={"#fff"} />}
                        Add products
                      </BasicButton>
                    </ModalFooter>
                  </Form>
                );
              }}
            </Formik>
          </Modal>
        </Page>
      )}
    </>
  );
};

export default QuoteEdit;
